/**
 * Common API types used across the application
 */

// High level content categories
export enum ContentType {
  TEXT = 'text',
  AUDIO = 'audio',
  VIDEO = 'video',
  IMAGE = 'image'
}

// Specific content sub-types
export enum SubType {
  // Text
  ARTICLE = 'article',
  NEWSLETTER = 'newsletter',
  SPARK = 'spark',
  NEWS = 'news',
  ANALYSIS = 'analysis',
  OPINION = 'opinion',
  EDUCATIONAL = 'educational',
  ENTERTAINMENT = 'entertainment',
  SATIRE = 'satire',
  ADVERTORIAL = 'advertorial',

  // Audio
  PODCAST = 'podcast',
  SONG = 'song',
  MEMO = 'memo',

  // Video
  LONG = 'long',
  SHORT = 'short',
  LIVE = 'live',

  // Image
  SINGLE = 'single',
  GALLERY = 'gallery'
}

// Content item for display
export interface ContentItemForDisplay {
  /** Winning fetch-ladder rung at extraction (standard_request/curl_cffi/…) */
  fetch_method?: string;
  id: string | number;
  title: string;
  url: string;
  summary?: string;
  content?: string;  // For Spark content text
  content_type: ContentType;
  sub_content_type?: SubType;
  trust_score: number;
  bias_rating: string;
  publisher: {
    name: string;
    image: string;
    date: string;
    domain?: string;
    logo_url?: string;
    description?: string;
  };
  topics: string[];
  image_url?: string;
  video_url?: string;
  audio_url?: string;
  asset_path?: string;        // New field for resolved asset URLs
  extracted_images?: string[]; // All images extracted from article
  duration?: string;
  background_style?: string;  // For Spark content styling
  text_color?: string;        // For Spark content text color

  // Consolidated media fields (aligns with TendV2Content)
  media_urls?: string[];      // Array of all media URLs
  primary_media_url?: string; // Hero media for display

  // Video platform metadata
  video_provider?: string;    // "youtube", "vimeo", "tiktok", etc.
  video_id?: string;          // Platform-specific video ID

  engagement?: {
    score?: number;
    likes?: number;
    comments?: number;
    shares?: number;
  };
  // Discovery Algorithm specific fields
  ranking_explanation?: string[];
  ranking_position?: number;
  momentum_score?: number;
  relevance_score?: number;
  // Pin functionality
  is_pinned?: boolean;
  pinned_at?: string;
  // Archive functionality
  is_archived?: boolean;
  archived_at?: string;
  // Editorial metadata (list view columns) — the adapter has always mapped
  // these through; declared here so the list view can read them typed
  author?: string;
  category?: string;
  section?: string;
  published_at?: string;
  word_count?: number;
  reading_time?: number;
  // Brand/logo resolution — the adapter maps the API's per-item brand fields
  // onto these (brandId = the content's OWN source brand_id, brandDefaultLogo
  // = resolved_logo). Declared so the list view can feed the tend button the
  // same per-item brand the card view does (it was passing the page brand →
  // wrong-brand logos on curated content).
  brandId?: number;
  brandName?: string;
  contentItemLogoId?: number | null;
  contentItemLogoDisplayMode?: 'icon_only' | 'name_only' | 'icon_and_name' | null;
  brandDefaultLogo?: any;
  brandLogoDisplayMode?: 'icon_only' | 'name_only' | 'icon_and_name';
}

// Filters for content retrieval
export interface ContentFilters {
  contentType?: ContentType[];
  publisher?: string[];
  dateRange?: {
    start?: string;
    end?: string;
  };
  trustScoreMin?: number;
  biasRange?: [number, number];
  searchQuery?: string;
  topic?: string[];
  limit?: number;
  offset?: number;
}

// API response types
export interface ContentResponse {
  id: string;
  title: string;
  url: string;
  publisher_id: string;
  content_type: string; // e.g. article, video, audio
  sub_type?: string; // e.g. news, opinion, long, short
  reliability_score: number;
  bias_score: number;
  created_at: string;
  published_at?: string; // Added for compatibility
  fact_checks: FactCheck[];
  summary?: string; // Added for compatibility
  tags?: string[]; // Added for compatibility
}

export interface TrustProfileResponse {
  publisher_id: string;
  reliability_score: number;
  bias_score: number;
  fact_check_score: number;
  overall_score: number;
  transparency_score: number;
  content_quality_score: number;
  historical_accuracy: number;
  bias_label: string;
}

export interface PublisherResponse {
  id: string;
  name: string;
  domain: string;
  description: string;
  logo_url: string;
  reliability_score: number;
  bias_score: number;
  category: string;
  created_at: string;
}

export interface FactCheck {
  claim: string;
  rating: string;
  source: string;
  date: string;
  url: string;
}
